// Phase 4.5B — Web Ascension page at /audit/next-steps/:sessionId.
// Paid-gated via the existing 402-rerouter pattern from PaidReport.jsx:
//   getReport() returns 402 if the session is not paid -> redirect to
//   /audit/preview/:sid. 404 -> "Report not found." 425 -> "still being
//   generated". This page does NOT touch the report data shape; it only
//   reads `business_name` for modal prefill.
//
// Three ascension tiers (Level 1 = Audit, COMPLETED; Level 2 = Strategic
// Consultation, RECOMMENDED; Level 3 = Enterprise). Lead-capture only.
// No Stripe, no checkout, no calendar. CEO-locked copy strings per
// 4.5B plan §12.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getReport } from "../lib/api/index";
import AscensionTierCard from "../components/ascension/AscensionTierCard";
import ConsultationRequestModal from "../components/ascension/ConsultationRequestModal";
import FounderTrustBlock from "../components/ascension/FounderTrustBlock";
import ImplementationModal from "../components/ImplementationModal";
import ReportLoading from "../components/report/ReportLoading";
import ReportError from "../components/report/ReportError";
import { logger } from "../lib/logger";

export default function NextSteps() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consultOpen, setConsultOpen] = useState(false);
  const [implOpen, setImplOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getReport(sessionId)
      .then((data) => {
        if (cancelled) return;
        setReport(data);
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error("next_steps_load_failed", {
          sessionId, status: err?.response?.status,
        });
        const code = err?.response?.status;
        if (code === 402) {
          navigate(`/audit/preview/${sessionId}`, { replace: true });
        } else if (code === 425) {
          setError("Your report is still being generated. Refresh in a moment.");
        } else if (code === 404) {
          setError("Report not found.");
        } else {
          setError("Could not load the page. Please refresh.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  const onViewReport = useCallback(() => {
    navigate(`/audit/report/${sessionId}`);
  }, [navigate, sessionId]);

  const onOpenConsult = useCallback(() => setConsultOpen(true), []);
  const onOpenImpl = useCallback(() => setImplOpen(true), []);

  if (loading) return <ReportLoading />;
  if (error) return <ReportError error={error} />;

  const businessName = report?.business_name || "";

  return (
    <div
      className="sf-container"
      style={{ padding: "48px 24px", maxWidth: 880 }}
      data-testid="next-steps-page"
    >
      {/* --- Header --- */}
      <section data-testid="next-steps-header" style={{ marginBottom: 36 }}>
        <div className="sf-eyebrow" style={{ marginBottom: 12 }} data-testid="next-steps-eyebrow">
          YOUR NEXT STEP
        </div>
        <h1
          className="sf-display"
          style={{ fontSize: 38, lineHeight: 1.15, marginBottom: 12, maxWidth: 780 }}
          data-testid="next-steps-headline"
        >
          You have the diagnosis. Choose your path forward.
        </h1>
      </section>

      {/* --- 3 tiers --- */}
      <section data-testid="next-steps-tiers" style={{ marginBottom: 12 }}>
        <AscensionTierCard
          variant="level-1"
          badge="✓ COMPLETED"
          title="SystemForge Audit"
          price="$97"
          subtitle="Full diagnostic + 13-section playbook"
          body="Your Business Health Score, Revenue Analysis, Opportunity Analysis, Quick Wins, 90-Day Implementation Plan, and Benchmark comparison."
          ctaLabel="View your report"
          ctaVariant="secondary"
          onCtaClick={onViewReport}
        />
        <AscensionTierCard
          variant="level-2"
          badge="RECOMMENDED"
          title="Strategic Business Consultation"
          price="$1,000"
          subtitle="60-minute founder-led strategy session"
          body="Review your audit findings with the SystemForge team. Identify root causes, prioritize next steps, and walk away with a Business Action Blueprint — a concrete, prioritized action plan built from your diagnostic data."
          boundary="Strategy only. No implementation, no build, no contract."
          ctaLabel="Request a consultation"
          ctaVariant="primary"
          onCtaClick={onOpenConsult}
        />
        <AscensionTierCard
          variant="level-3"
          badge={null}
          title="SystemForge Enterprise"
          price="Starting at $10,000"
          subtitle="We build it with you."
          body="Implementation, automation, systems architecture, AI integration, operations optimization. Your diagnostic becomes the blueprint; our team executes it."
          ctaLabel="Request implementation"
          ctaVariant="secondary"
          onCtaClick={onOpenImpl}
        />
      </section>

      {/* --- Founder trust block --- */}
      <FounderTrustBlock />

      {/* --- Modals --- */}
      <ConsultationRequestModal
        open={consultOpen}
        onClose={() => setConsultOpen(false)}
        sessionId={sessionId}
        defaultName=""
        defaultEmail=""
        defaultBusiness={businessName}
      />
      <ImplementationModal
        open={implOpen}
        onClose={() => setImplOpen(false)}
        sessionId={sessionId}
        defaultEmail=""
        defaultBusiness={businessName}
      />
    </div>
  );
}
